import {
  useRef,
  useState,
  type ChangeEvent,
  type ClipboardEvent,
  type DragEvent,
  type FormEvent,
} from 'react';

export function useNumberInput(value: string, onChange: (value: string) => void) {
  const [badInput, setBadInput] = useState(false);
  const [transferNotice, setTransferNotice] = useState(false);
  const pendingEdit = useRef({
    leadingPlus: false,
    trailingPoint: false,
    wasBadInput: false,
    selection: '',
  });
  const handledInput = useRef<Event | null>(null);
  const numberValue = value.trim();
  const withoutPlus =
    pendingEdit.current.leadingPlus && numberValue.startsWith('+')
      ? numberValue.slice(1)
      : numberValue;
  const displayedValue =
    pendingEdit.current.trailingPoint && withoutPlus.endsWith('.')
      ? withoutPlus.slice(0, -1)
      : withoutPlus;
  const cannotDisplay =
    displayedValue !== '' &&
    (!/^-?(?:\d+(?:\.\d+)?|\.\d+)(?:[eE][+-]?\d+)?$/.test(displayedValue) ||
      !Number.isFinite(Number(displayedValue)));

  const resetPendingEdit = () => {
    pendingEdit.current = {
      leadingPlus: false,
      trailingPoint: false,
      wasBadInput: false,
      selection: '',
    };
  };

  const handleInput = (event: FormEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const native = event.nativeEvent;
    const edit = pendingEdit.current;
    setTransferNotice(false);
    if (!(native instanceof InputEvent)) {
      resetPendingEdit();
      setBadInput(input.validity.badInput);
      return;
    }

    // Number inputs can omit a visible leading "+" or trailing "." from their value.
    const wasHidden = edit.leadingPlus || edit.trailingPoint;
    const data = native.data ?? '';
    const hadSelection = edit.selection !== '';
    if (edit.selection.includes('+')) {
      edit.leadingPlus = false;
    }
    if (edit.selection.includes('.')) {
      edit.trailingPoint = false;
    }
    edit.selection = '';
    if (edit.trailingPoint && input.value.includes('.')) {
      edit.trailingPoint = false;
    }
    if (native.inputType.startsWith('delete') && input.value === displayedValue) {
      if (native.inputType.endsWith('Backward') && edit.trailingPoint) {
        edit.trailingPoint = false;
      } else if (edit.leadingPlus) {
        edit.leadingPlus = false;
      } else {
        edit.trailingPoint = false;
      }
    }
    if (!input.value && !input.validity.badInput) {
      edit.leadingPlus = false;
      edit.trailingPoint = false;
    }
    if (data.startsWith('+') && (!edit.wasBadInput || hadSelection)) {
      edit.leadingPlus = true;
    }
    if (
      data.endsWith('.') &&
      input.value &&
      !input.value.includes('.') &&
      !input.validity.badInput
    ) {
      edit.trailingPoint = true;
    }
    edit.wasBadInput = input.validity.badInput;
    setBadInput(input.validity.badInput);

    const raw = input.validity.badInput
      ? edit.leadingPlus
        ? '+'
        : ''
      : `${edit.leadingPlus ? '+' : ''}${input.value}${edit.trailingPoint ? '.' : ''}`;
    if (raw !== input.value || (wasHidden && input.value === displayedValue)) {
      handledInput.current = native;
      onChange(raw);
    }
  };

  return {
    badInput,
    cannotDisplay,
    transferNotice,
    inputProps: {
      value: cannotDisplay ? '' : displayedValue,
      onBeforeInput: () => {
        pendingEdit.current.selection = document.getSelection()?.toString() ?? '';
      },
      onPaste: (event: ClipboardEvent<HTMLInputElement>) => {
        const text = event.clipboardData.getData('text');
        const selection = document.getSelection()?.toString() ?? '';
        if (/^[0-9]+$/.test(text)) {
          pendingEdit.current.selection = selection;
          setTransferNotice(false);
          return;
        }

        event.preventDefault();
        const input = event.currentTarget;
        const visibleValue =
          pendingEdit.current.leadingPlus || pendingEdit.current.trailingPoint
            ? numberValue
            : input.value;
        const replacesWholeValue =
          (input.value === '' && !input.validity.badInput) ||
          (selection !== '' && selection === visibleValue);
        if (text && replacesWholeValue) {
          resetPendingEdit();
          setBadInput(false);
          setTransferNotice(false);
          onChange(text);
        } else {
          setTransferNotice(true);
        }
      },
      onDrop: (event: DragEvent<HTMLInputElement>) => {
        if (!/^[0-9]+$/.test(event.dataTransfer.getData('text'))) {
          event.preventDefault();
          setTransferNotice(true);
        }
      },
      onInput: handleInput,
      onChange: (event: ChangeEvent<HTMLInputElement>) => {
        if (handledInput.current !== event.nativeEvent) {
          setTransferNotice(false);
          setBadInput(event.target.validity.badInput);
          onChange(event.target.value);
        }
      },
    },
  };
}
